import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Download, 
  Clock, 
  TrendingUp, 
  Calendar as CalendarIcon,
  BarChart3,
  PieChart,
  Target,
  Flame,
  Trophy,
  ChevronLeft,
  ChevronRight,
  FileText,
  Table2,
  Search,
  Activity,
  Mouse,
  Keyboard
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  LineChart,
  Line
} from 'recharts';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, eachDayOfInterval, isWeekend, differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ActivityBar, ActivityBadge, formatTrackedTime } from '../components/activity/ActivityBar';

const Reports = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState(null);
  
  // Date range state
  const [dateRangeType, setDateRangeType] = useState('week');
  const [customStartDate, setCustomStartDate] = useState(startOfMonth(new Date()));
  const [customEndDate, setCustomEndDate] = useState(endOfMonth(new Date()));
  const [showCustomRange, setShowCustomRange] = useState(false);
  
  // Filters
  const [selectedProjects, setSelectedProjects] = useState([]);
  
  // View state
  const [activeTab, setActiveTab] = useState('summary');
  
  // Pagination for detailed view
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const pageSize = 25;

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateRangeType) {
      case 'today':
        return { start: startOfDay(now), end: endOfDay(now) };
      case 'week':
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'lastWeek':
        const lastWeekStart = subWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1);
        return { start: lastWeekStart, end: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
      case 'lastMonth':
        const lastMonthStart = subMonths(startOfMonth(now), 1);
        return { start: lastMonthStart, end: endOfMonth(lastMonthStart) };
      case 'custom':
        return { 
          start: startOfDay(customStartDate), 
          end: endOfDay(customEndDate) 
        };
      default:
        return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    }
  }, [dateRangeType, customStartDate, customEndDate]);

  // Fetch data when date range changes
  useEffect(() => {
    const fetchData = async () => {
      if (!api) return;
      setLoading(true);
      
      // Calculate date range based on current state (not stale closure)
      const now = new Date();
      let startDate, endDate;
      
      switch (dateRangeType) {
        case 'today':
          startDate = startOfDay(now);
          endDate = endOfDay(now);
          break;
        case 'week':
          startDate = startOfWeek(now, { weekStartsOn: 1 });
          endDate = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'month':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'lastWeek':
          const lastWeekStart = subWeeks(startOfWeek(now, { weekStartsOn: 1 }), 1);
          startDate = lastWeekStart;
          endDate = endOfWeek(lastWeekStart, { weekStartsOn: 1 });
          break;
        case 'lastMonth':
          const lastMonthStart = subMonths(startOfMonth(now), 1);
          startDate = lastMonthStart;
          endDate = endOfMonth(lastMonthStart);
          break;
        case 'custom':
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
          break;
        default:
          startDate = startOfWeek(now, { weekStartsOn: 1 });
          endDate = endOfWeek(now, { weekStartsOn: 1 });
      }
      
      try {
        // Fetch employee's own time entries only (API enforces user_id filtering)
        const [entriesRes, projectsRes] = await Promise.all([
          api.get('/time-entries', {
            params: {
              start_date: startDate.toISOString(),
              end_date: endDate.toISOString(),
              _t: Date.now() // Cache bust
            }
          }),
          api.get('/projects')
        ]);
        
        const filteredEntries = entriesRes.data.filter(e => e.duration > 0);
        setEntries(filteredEntries);
        setProjects(projectsRes.data);

        // Fetch activity data
        try {
          const activityRes = await api.get('/activity/summary', {
            params: { start_date: startDate.toISOString(), end_date: endDate.toISOString() }
          });
          setActivityData(activityRes.data);
        } catch { setActivityData(null); }
      } catch (error) {
        console.error('Error fetching reports data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [dateRangeType, customStartDate, customEndDate, api]);

  // Filter entries by selected projects
  const filteredEntries = useMemo(() => {
    if (selectedProjects.length === 0) return entries;
    return entries.filter(e => selectedProjects.includes(e.project_id) || (!e.project_id && selectedProjects.includes('no-project')));
  }, [entries, selectedProjects]);

  // Utility functions
  const getProjectById = (id) => projects.find(p => p.id === id);

  const formatDuration = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours === 0) return `${minutes}m`;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const formatHours = (seconds) => {
    return Math.round((seconds / 3600) * 10) / 10;
  };

  const formatTimeOnly = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Summary calculations
  const totalDuration = useMemo(() => {
    return filteredEntries.reduce((acc, e) => acc + (e.duration || 0), 0);
  }, [filteredEntries]);

  const totalEntries = filteredEntries.length;

  // Daily breakdown for line chart - convert zero hours to null to create gaps
  const dailyChartData = useMemo(() => {
    const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayEntries = filteredEntries.filter(e => e.start_time?.slice(0, 10) === dateStr);
      const duration = dayEntries.reduce((acc, e) => acc + (e.duration || 0), 0);
      const hours = formatHours(duration);
      return {
        date: format(day, 'EEE d'),
        fullDate: dateStr,
        hours: hours > 0 ? hours : null, // Convert 0 to null for chart gaps
        rawHours: hours, // Keep raw value for tooltip
        isWeekend: isWeekend(day)
      };
    });
  }, [filteredEntries, dateRange]);

  // Project breakdown for pie/bar chart
  const projectChartData = useMemo(() => {
    const breakdown = {};
    filteredEntries.forEach(e => {
      const projectId = e.project_id || 'no-project';
      breakdown[projectId] = (breakdown[projectId] || 0) + (e.duration || 0);
    });
    
    return Object.entries(breakdown)
      .map(([id, duration]) => {
        const project = getProjectById(id);
        return {
          id,
          name: project?.name || 'No Project',
          color: project?.color || '#94a3b8',
          hours: formatHours(duration),
          duration,
          percent: totalDuration ? Math.round((duration / totalDuration) * 100) : 0
        };
      })
      .sort((a, b) => b.duration - a.duration);
  }, [filteredEntries, totalDuration, projects]);

  // Top 5 tasks
  const topTasks = useMemo(() => {
    const taskMap = {};
    filteredEntries.forEach(e => {
      // Use a unique key for empty descriptions, but preserve the actual description value
      const key = e.description || '__no_description__';
      if (!taskMap[key]) {
        taskMap[key] = { description: e.description || '', duration: 0, project_id: e.project_id };
      }
      taskMap[key].duration += e.duration || 0;
    });
    
    return Object.values(taskMap)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5);
  }, [filteredEntries]);

  // Productivity Score calculation
  const productivityScore = useMemo(() => {
    const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    const workingDays = days.filter(d => !isWeekend(d));
    const daysWithTracking = new Set();
    
    filteredEntries.forEach(e => {
      if (e.start_time) {
        const dateStr = e.start_time.slice(0, 10);
        daysWithTracking.add(dateStr);
      }
    });
    
    // Count working days with tracking
    const trackedWorkingDays = workingDays.filter(d => 
      daysWithTracking.has(format(d, 'yyyy-MM-dd'))
    ).length;
    
    const score = workingDays.length > 0 
      ? Math.round((trackedWorkingDays / workingDays.length) * 100)
      : 0;
    
    return {
      score,
      trackedDays: trackedWorkingDays,
      totalWorkingDays: workingDays.length
    };
  }, [filteredEntries, dateRange]);

  // Streak calculation
  const streak = useMemo(() => {
    const today = new Date();
    const trackedDates = new Set();
    
    entries.forEach(e => {
      if (e.start_time) {
        trackedDates.add(e.start_time.slice(0, 10));
      }
    });
    
    let count = 0;
    let checkDate = today;
    
    // Go backwards checking each working day
    while (true) {
      const dateStr = format(checkDate, 'yyyy-MM-dd');
      
      if (isWeekend(checkDate)) {
        checkDate = new Date(checkDate.getTime() - 24 * 60 * 60 * 1000);
        continue;
      }
      
      if (trackedDates.has(dateStr)) {
        count++;
        checkDate = new Date(checkDate.getTime() - 24 * 60 * 60 * 1000);
      } else {
        break;
      }
      
      // Safety limit
      if (count > 365) break;
    }
    
    return count;
  }, [entries]);

  // Sorted and paginated entries for detailed view
  const sortedEntries = useMemo(() => {
    return [...filteredEntries].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'date':
          aVal = new Date(a.start_time);
          bVal = new Date(b.start_time);
          break;
        case 'task':
          aVal = a.description || '';
          bVal = b.description || '';
          break;
        case 'project':
          aVal = getProjectById(a.project_id)?.name || '';
          bVal = getProjectById(b.project_id)?.name || '';
          break;
        case 'duration':
          aVal = a.duration || 0;
          bVal = b.duration || 0;
          break;
        default:
          return 0;
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  }, [filteredEntries, sortField, sortOrder, projects]);

  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedEntries.slice(start, start + pageSize);
  }, [sortedEntries, currentPage]);

  const totalPages = Math.ceil(sortedEntries.length / pageSize);

  // Export functions
  const exportToCSV = () => {
    const headers = ['Date', 'Task', 'Project', 'Start Time', 'End Time', 'Duration'];
    const rows = filteredEntries.map(e => {
      const project = getProjectById(e.project_id);
      return [
        format(new Date(e.start_time), 'yyyy-MM-dd'),
        e.description || 'No description',
        project?.name || 'No Project',
        formatTimeOnly(e.start_time),
        e.end_time ? formatTimeOnly(e.end_time) : '',
        formatDuration(e.duration)
      ];
    });
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time_report_${format(dateRange.start, 'yyyy-MM-dd')}_to_${format(dateRange.end, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const exportToPDF = () => {
    // Generate PDF content as HTML and print
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Time Report - Inspiro Tracker</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; }
          h1 { color: #3b82f6; margin-bottom: 5px; }
          .subtitle { color: #64748b; margin-bottom: 30px; }
          .summary { display: flex; gap: 40px; margin-bottom: 30px; }
          .summary-item { }
          .summary-label { color: #64748b; font-size: 12px; }
          .summary-value { font-size: 24px; font-weight: bold; color: #0f172a; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; color: #64748b; border-bottom: 2px solid #e2e8f0; }
          td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
          .project-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 8px; }
          .totals { margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; }
          .footer { margin-top: 40px; text-align: center; color: #94a3b8; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Inspiro Tracker Time Report</h1>
        <div class="subtitle">
          ${user?.name || 'Employee'} · ${format(dateRange.start, 'MMM d, yyyy')} - ${format(dateRange.end, 'MMM d, yyyy')}
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Time</div>
            <div class="summary-value">${formatDuration(totalDuration)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Entries</div>
            <div class="summary-value">${totalEntries}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Projects</div>
            <div class="summary-value">${projectChartData.length}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Task</th>
              <th>Project</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            ${filteredEntries.map(e => {
              const project = getProjectById(e.project_id);
              return `
                <tr>
                  <td>${format(new Date(e.start_time), 'MMM d, yyyy')}</td>
                  <td>${e.description || 'No description'}</td>
                  <td>
                    <span class="project-dot" style="background-color: ${project?.color || '#94a3b8'}"></span>
                    ${project?.name || 'No Project'}
                  </td>
                  <td>${formatTimeOnly(e.start_time)}</td>
                  <td>${e.end_time ? formatTimeOnly(e.end_time) : '-'}</td>
                  <td>${formatDuration(e.duration)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <strong>Total: ${formatDuration(totalDuration)}</strong> across ${totalEntries} entries
        </div>
        
        <div class="footer">
          Generated by Inspiro Tracker · ${format(new Date(), 'MMMM d, yyyy')}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF ready to print');
  };

  // Toggle project selection
  const toggleProject = (projectId) => {
    setSelectedProjects(prev => 
      prev.includes(projectId)
        ? prev.filter(p => p !== projectId)
        : [...prev, projectId]
    );
  };

  // Custom tooltips
  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const rawValue = payload[0].payload?.rawHours;
      // Show actual value (could be null for days with no tracking)
      const displayValue = value !== null ? value : (rawValue !== undefined ? rawValue : 0);
      return (
        <div className="bg-white dark:bg-slate-800 px-4 py-3 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-400 mb-1">{label}</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">{displayValue}h</p>
          {value === null && (
            <p className="text-xs text-slate-400 italic">No time tracked</p>
          )}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-800 px-4 py-3 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.color }} />
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{data.name}</p>
          </div>
          <p className="text-lg font-bold text-slate-900 dark:text-white">{data.hours}h</p>
          <p className="text-xs text-slate-500">{data.percent}% of total</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('reports.title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('reports.subtitle')}</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Range Quick Selects */}
            <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
              {[
                { value: 'today', label: t('timeTracker.today') },
                { value: 'week', label: t('reports.thisWeek') },
                { value: 'month', label: t('reports.thisMonth') },
                { value: 'lastWeek', label: 'Last Week' },
                { value: 'lastMonth', label: 'Last Month' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setDateRangeType(opt.value); setShowCustomRange(false); }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    dateRangeType === opt.value && !showCustomRange
                      ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`date-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            {/* Custom Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={showCustomRange ? 'default' : 'outline'}
                  className="h-9 rounded-lg text-xs"
                  data-testid="custom-range-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Custom
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4 rounded-xl" align="end">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">Start Date</p>
                      <Calendar
                        mode="single"
                        selected={customStartDate}
                        onSelect={(date) => {
                          setCustomStartDate(date);
                          setDateRangeType('custom');
                          setShowCustomRange(true);
                        }}
                        className="rounded-lg border"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">End Date</p>
                      <Calendar
                        mode="single"
                        selected={customEndDate}
                        onSelect={(date) => {
                          setCustomEndDate(date);
                          setDateRangeType('custom');
                          setShowCustomRange(true);
                        }}
                        className="rounded-lg border"
                      />
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Export Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 rounded-lg" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2 rounded-xl" align="end">
                <button
                  onClick={exportToCSV}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-800"
                >
                  <Table2 className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={exportToPDF}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-800"
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Date Range Display & Project Filter */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Showing: <span className="font-medium text-slate-700 dark:text-slate-300">
              {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
            </span>
          </div>
          
          {/* Project Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 rounded-lg text-xs">
                <PieChart className="w-4 h-4 mr-2" />
                {selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} selected`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 rounded-xl" align="end">
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedProjects([])}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg ${
                    selectedProjects.length === 0 ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600' : 'hover:bg-slate-100 dark:bg-slate-700'
                  }`}
                >
                  All Projects
                </button>
                {projects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => toggleProject(project.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-slate-100 dark:bg-slate-700"
                  >
                    <Checkbox checked={selectedProjects.includes(project.id)} />
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    <span className="truncate">{project.name}</span>
                  </button>
                ))}
                {projects.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-400">No projects assigned</p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
          <TabsList className="bg-slate-100 dark:bg-slate-700/80 dark:bg-slate-800/80 p-1.5 rounded-xl">
            <TabsTrigger value="summary" className="text-sm rounded-lg px-4" data-testid="tab-summary">
              <BarChart3 className="w-4 h-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="detailed" className="text-sm rounded-lg px-4" data-testid="tab-detailed">
              <Table2 className="w-4 h-4 mr-2" />
              Detailed
            </TabsTrigger>
            <TabsTrigger value="activity" className="text-sm rounded-lg px-4" data-testid="tab-activity">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-5">
            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Time */}
              <div className="stat-card">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <span className="stat-label">Total Time</span>
                    <span className="stat-value">{formatDuration(totalDuration)}</span>
                  </div>
                  <div className="stat-icon blue">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
              
              {/* Entries */}
              <div className="stat-card">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <span className="stat-label">Entries</span>
                    <span className="stat-value">{totalEntries}</span>
                  </div>
                  <div className="stat-icon purple">
                    <CalendarIcon className="w-6 h-6 text-violet-600" />
                  </div>
                </div>
              </div>
              
              {/* Productivity Score */}
              <div className="stat-card">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <span className="stat-label">Productivity</span>
                    <span className="stat-value">{productivityScore.score}%</span>
                    <p className="text-xs text-slate-500">
                      {productivityScore.trackedDays} of {productivityScore.totalWorkingDays} working days
                    </p>
                  </div>
                  <div className="relative w-14 h-14">
                    <svg className="w-14 h-14 -rotate-90">
                      <circle
                        cx="28"
                        cy="28"
                        r="24"
                        stroke="#e2e8f0"
                        strokeWidth="6"
                        fill="none"
                      />
                      <circle
                        cx="28"
                        cy="28"
                        r="24"
                        stroke="#22c55e"
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={`${productivityScore.score * 1.51} 151`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Streak */}
              <div className="stat-card">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <span className="stat-label">Tracking Streak</span>
                    <div className="flex items-center gap-2">
                      <span className="stat-value">{streak}</span>
                      {streak > 0 && <Flame className="w-6 h-6 text-orange-500" />}
                    </div>
                    <p className="text-xs text-slate-500">
                      {streak > 0 ? `${streak} day${streak > 1 ? 's' : ''} in a row!` : 'Start tracking today!'}
                    </p>
                  </div>
                  <div className="stat-icon amber">
                    <Flame className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Daily Trend Chart */}
              <div className="premium-card p-6">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">
                  Daily Trend
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData}>
                      <defs>
                        <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        tickFormatter={v => `${v}h`}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="hours" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fill="url(#colorHours)" 
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, fill: '#3b82f6' }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* By Project Chart */}
              <div className="premium-card p-6">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">
                  By Project
                </h3>
                <div className="h-64">
                  {projectChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={projectChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="hours"
                        >
                          {projectChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400">
                      No data to display
                    </div>
                  )}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  {projectChartData.slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center gap-1.5 text-xs">
                      <div 
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: p.color }}
                      />
                      <span className="text-slate-600 dark:text-slate-400">{p.name}</span>
                      <span className="text-slate-400">({p.hours}h)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top 5 Tasks */}
            <div className="premium-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-amber-500" />
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                  Top 5 Tasks
                </h3>
              </div>
              <div className="space-y-3">
                {topTasks.map((task, idx) => {
                  const project = getProjectById(task.project_id);
                  const percent = totalDuration ? Math.round((task.duration / totalDuration) * 100) : 0;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-semibold text-slate-500">
                        {idx + 1}
                      </div>
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: project?.color || '#94a3b8' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {task.description || <span className="italic text-slate-400 dark:text-slate-500 font-normal">No description added</span>}
                        </p>
                        <p className="text-xs text-slate-500">{project?.name || 'No Project'}</p>
                      </div>
                      <div className="w-32">
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{ width: `${percent}%`, backgroundColor: project?.color || '#94a3b8' }}
                          />
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white timer-display w-16 text-right">
                        {formatDuration(task.duration)}
                      </div>
                    </div>
                  );
                })}
                {topTasks.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No tasks in this period</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Detailed Tab */}
          <TabsContent value="detailed" className="space-y-4">
            <div className="premium-card overflow-hidden">
              {/* Table Header */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      {[
                        { key: 'date', label: 'Date' },
                        { key: 'task', label: 'Task' },
                        { key: 'project', label: 'Project' },
                        { key: 'start', label: 'Start' },
                        { key: 'end', label: 'End' },
                        { key: 'duration', label: 'Duration' }
                      ].map(col => (
                        <th 
                          key={col.key}
                          className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                          onClick={() => {
                            if (sortField === col.key) {
                              setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField(col.key);
                              setSortOrder('desc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            {sortField === col.key && (
                              <span className="text-blue-600">
                                {sortOrder === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map((entry, idx) => {
                      const project = getProjectById(entry.project_id);
                      return (
                        <tr 
                          key={entry.id}
                          className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        >
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                            {format(new Date(entry.start_time), 'MMM d, yyyy')}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
                            {entry.description || <span className="italic text-slate-400 dark:text-slate-500 font-normal">No description added</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: project?.color || '#94a3b8' }}
                              />
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {project?.name || 'No Project'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                            {formatTimeOnly(entry.start_time)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                            {entry.end_time ? formatTimeOnly(entry.end_time) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-white timer-display">
                            {formatDuration(entry.duration)}
                          </td>
                        </tr>
                      );
                    })}
                    {paginatedEntries.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                          No entries in this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-sm text-slate-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedEntries.length)} of {sortedEntries.length} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8 rounded-lg"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 rounded-lg"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-5" data-testid="activity-tab-content">
            {activityData && activityData.windows_count > 0 ? (
              <>
                {/* Activity Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="stat-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Overall Activity</p>
                        <p className="text-2xl font-bold mt-1" data-testid="my-overall-activity">{activityData.overall_percent}%</p>
                      </div>
                      <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                        <Activity className="w-5 h-5 text-emerald-600" />
                      </div>
                    </div>
                    <ActivityBar overall={activityData.overall_percent} mouse={activityData.mouse_percent} keyboard={activityData.keyboard_percent} size="sm" className="mt-3" />
                  </div>
                  <div className="stat-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Mouse Activity</p>
                        <p className="text-2xl font-bold mt-1">{activityData.mouse_percent}%</p>
                      </div>
                      <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                        <Mouse className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Keyboard Activity</p>
                        <p className="text-2xl font-bold mt-1">{activityData.keyboard_percent}%</p>
                      </div>
                      <div className="p-2 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                        <Keyboard className="w-5 h-5 text-violet-600" />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Tracked Time</p>
                        <p className="text-2xl font-bold mt-1">{formatTrackedTime(activityData.total_tracked_seconds)}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Breakdown */}
                {activityData.daily_breakdown && activityData.daily_breakdown.length > 0 && (
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <h3 className="font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Daily Activity Breakdown</h3>
                    </div>
                    <div className="p-4">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={activityData.daily_breakdown} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                            <Tooltip 
                              formatter={(value, name) => [`${value}%`, name === 'overall_percent' ? 'Overall' : name === 'mouse_percent' ? 'Mouse' : 'Keyboard']}
                              labelFormatter={l => `Date: ${l}`}
                            />
                            <Bar dataKey="overall_percent" fill="#10B981" name="Overall" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="mouse_percent" fill="#3B82F6" name="Mouse" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="keyboard_percent" fill="#8B5CF6" name="Keyboard" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                      {activityData.daily_breakdown.map(day => (
                        <div key={day.date} className="flex items-center justify-between p-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`activity-day-${day.date}`}>
                          <span className="text-sm font-medium">{day.date}</span>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="text-slate-500">{formatTrackedTime(day.total_seconds)}</span>
                            <span className="flex items-center gap-1 min-w-[60px]"><Mouse className="w-3 h-3 text-blue-500" /> {day.mouse_percent}%</span>
                            <span className="flex items-center gap-1 min-w-[60px]"><Keyboard className="w-3 h-3 text-violet-500" /> {day.keyboard_percent}%</span>
                            <ActivityBadge percent={day.overall_percent} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 text-slate-500">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No activity data for this period</p>
                <p className="text-xs mt-1">Activity tracking requires the InspiroTrack desktop agent to be running.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
